import { useState } from "react";
import dayjs from "dayjs";
import { Calendar, ChevronDown, RefreshCw } from "lucide-react";
import "./AnalyticsFilters.scss";

const DATE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Custom", days: null },
];

const AnalyticsFilters = ({
  startDate,
  endDate,
  onDateChange,
  modelId,
  modelIds,
  onModelChange,
  onReload,
}) => {
  const [activePreset, setActivePreset] = useState("Last 7 days");
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customStart, setCustomStart] = useState(
    dayjs().subtract(7, "day").format("YYYY-MM-DD")
  );
  const [customEnd, setCustomEnd] = useState(dayjs().format("YYYY-MM-DD"));

  const handlePresetClick = (preset) => {
    setActivePreset(preset.label);
    if (preset.days !== null) {
      setShowCustomForm(false);
      const end = dayjs().endOf("day").toDate();
      const start = dayjs()
        .subtract(preset.days, "day")
        .startOf("day")
        .toDate();
      onDateChange(start, end);
    } else {
      // When Custom is clicked, sync the inputs with current dates and show form
      if (startDate) {
        setCustomStart(dayjs(startDate).format("YYYY-MM-DD"));
      }
      if (endDate) {
        setCustomEnd(dayjs(endDate).format("YYYY-MM-DD"));
      }
      setShowCustomForm(true);
    }
  };

  const handleDateDisplayClick = () => {
    // When clicking date display, switch to Custom and show form
    setActivePreset("Custom");
    if (startDate) {
      setCustomStart(dayjs(startDate).format("YYYY-MM-DD"));
    }
    if (endDate) {
      setCustomEnd(dayjs(endDate).format("YYYY-MM-DD"));
    }
    setShowCustomForm(true);
  };

  const handleCustomApply = () => {
    const start = dayjs(customStart).startOf("day").toDate();
    const end = dayjs(customEnd).endOf("day").toDate();
    onDateChange(start, end);
    setShowCustomForm(false);
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) return "";
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
  };

  return (
    <div className="analytics_filters">
      <div className="filters_row">
        <div className="date_filters">
          <div className="model_filter">
            <select
              value={modelId || ""}
              onChange={(e) => onModelChange(e.target.value || null)}
              className="form-control"
            >
              <option value="">All Models</option>
              {modelIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="select_icon" />
          </div>
          <div className="date_presets">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={`preset_btn ${
                  activePreset === preset.label ? "active" : ""
                }`}
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {showCustomForm ? (
            <div className="date_form_inline">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                max={customEnd}
              />
              <span className="date_separator">—</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                min={customStart}
                max={dayjs().format("YYYY-MM-DD")}
              />
              <button
                type="button"
                className="apply_btn"
                onClick={handleCustomApply}
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="date_display" onClick={handleDateDisplayClick}>
              <Calendar size={14} />
              <span>{formatDateRange()}</span>
            </div>
          )}
        </div>
        <div className="filters_actions">
          <button
            type="button"
            className="reload_btn"
            onClick={onReload}
            title="Reload data"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsFilters;
